import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { currencyMapper, storeKeyMapper } from '@shared/utils/src/ct/index';
import type { AddOnProduct, Cart, LineItem } from '../../../codegen/types';
import { MarketingType } from '../../../codegen/types';
import { useCartContext, useLocaleContext, useStaticContext } from '../../../context';
import { useGetProductMarketingQuery } from '../../../graphqlDocument/cart/getProductMarketing.generated';
import { useAddToBag } from '../../../hooks';
import { ImagePartial } from '../../../partials';
import { getAmount, getCurrencySymbol } from '../../../utils/currencyHelper';
import { sendAddToCartFromCartEvent } from '../../../utils/gtm/events/add_to_cart/sendAddToCartFromCartEvent';
import { getCtLocale } from '../../../utils/internationalization';
import {
  AddToBag,
  AddToBagWrapper,
  Description,
  ErrorMessage,
  ImageWrapper,
  Info,
  Item,
  Price,
  StyledAnchor,
  Title,
  Wrapper,
} from './AddOns.styles';

type AddOnsProps = {
  isMiniCart?: boolean;
  lineItems: LineItem[] | undefined;
};

export function AddOns({ isMiniCart = false, lineItems }: AddOnsProps) {
  const { t } = useTranslation(['lib-global-common', 'lib-products-common']);
  const { isMaxItemsReached, cartId, anonymousId, isBasketValueExceeded } = useCartContext();
  const { locale } = useLocaleContext();
  const { baseUrl } = useStaticContext();

  const [addOns, setAddons] = useState<AddOnProduct[]>([]);
  const { addToBag, addToBagLoading } = useAddToBag({ showMiniCart: isMiniCart });
  const [addToBagError, setAddToBagError] = useState(false);
  const [productErrors, setProductErrors] = useState<Record<string, boolean>>({});

  const shouldSkipQuery = !lineItems?.length || isMaxItemsReached || isBasketValueExceeded;
  const { data, refetch } = useGetProductMarketingQuery({
    variables: {
      input: {
        anonymousId,
        cartId,
        locale: getCtLocale(locale),
        marketingType: MarketingType.ADDONS,
      },
    },
    skip: shouldSkipQuery,
  });

  useEffect(() => {
    if (!data?.getProductMarketing?.products || shouldSkipQuery) {
      return;
    }

    const addOnsProducts = data.getProductMarketing.products.filter(
      (product): product is AddOnProduct => product !== null,
    );

    setAddons(addOnsProducts);
  }, [data, shouldSkipQuery]);

  useEffect(() => {
    if (!shouldSkipQuery) {
      // Run getProductMarketing query even though its input hasn't changed
      refetch();
    }
  }, [shouldSkipQuery, refetch, lineItems?.length]);

  const clearErrorStatus = (sku: string) => {
    setProductErrors((prev) => ({
      ...prev,
      [sku]: false,
    }));
  };

  const handleAddToBag = async (addOnSku: string | undefined) => {
    if (!addOnSku || addToBagLoading) return;

    const input = {
      currency: currencyMapper[locale],
      storeKey: storeKeyMapper(locale),
      locale: getCtLocale(locale),
      sku: addOnSku,
      quantity: 1,
      cartId,
      ...(anonymousId && { anonymousId }),
    };

    clearErrorStatus(input.sku);

    try {
      if (cartId) {
        await addToBag({
          variables: { input },
          onCompleted: ({ addToCart }) => {
            clearErrorStatus(input.sku);
            refetch();
            sendAddToCartFromCartEvent(addOnSku, addToCart as Cart, baseUrl);
          },
          onError: () => {
            setProductErrors((prev) => ({
              ...prev,
              [input.sku]: true,
            }));
          },
        });

        return;
      }
    } catch (e) {
      setAddToBagError(true);
    }
  };

  return (
    <Wrapper>
      {addOns?.map((addon: AddOnProduct) => {
        const addOnVariant = addon.variants[0];
        const hasError = addOnVariant?.sku ? productErrors[addOnVariant.sku] : false;

        return (
          <Item $isMiniCart={isMiniCart} key={`${addon.id}`}>
            <Link href={`/${addon.slug}/${addon.key}-p`} passHref>
              <ImageWrapper
                aria-label={`${t('cart.line.item.link.text', { item: addon.name })}`}
                $isMiniCart={isMiniCart}
              >
                {addon.imageURLs[0] && (
                  <ImagePartial src={addon.imageURLs[0]} layout="fill" objectFit="contain" altText={addon.title} />
                )}
              </ImageWrapper>
            </Link>
            <Info>
              <Link
                href={`/${addon.slug}/${addon.key}-p`}
                passHref
                aria-label={`${t('cart.line.item.link.text', { item: addon.name })}`}
              >
                <StyledAnchor>
                  <Title dangerouslySetInnerHTML={{ __html: addon.title === '' ? addon.name : addon.title }} />
                </StyledAnchor>
              </Link>
              <Price>
                {getCurrencySymbol(addOnVariant?.price?.currencyCode)}
                {getAmount(addOnVariant?.price.centAmount)}
              </Price>
              <Description dangerouslySetInnerHTML={{ __html: addon.description }} />
            </Info>
            <AddToBagWrapper>
              <AddToBag
                type="button"
                data-testid="addToBagButton"
                aria-label={t('pdp.add.to.bag', { ns: 'lib-products-common' })}
                onClick={() => handleAddToBag(addOnVariant?.sku)}
                disabled={addToBagLoading}
              >
                {t('pdp.add.to.bag', { ns: 'lib-products-common' })}
              </AddToBag>
              {addToBagError ||
                (hasError && (
                  <ErrorMessage role="alert">{t('pdp.add.to.bag.error', { ns: 'lib-products-common' })}</ErrorMessage>
                ))}
            </AddToBagWrapper>
          </Item>
        );
      })}
    </Wrapper>
  );
}
